import {
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Eye, Send } from 'lucide-react';
import { AnnouncementPreviewModal } from './AnnouncementPreviewModal';
import {
  AnnouncementMobilePreview,
  getDisplayState,
} from './AnnouncementMobilePreview';
import {
  announcementsApi,
  type AnnouncementCategory,
  type AnnouncementCreateInput,
  type AnnouncementTargetApp,
  type AnnouncementUpdateInput,
} from '@/services/api';
import {
  PageHeader,
  Section,
  TextInput,
  Select,
  LoadingState,
  ErrorState,
  RichTextEditor,
} from '@/components/common';
import { sanitizeAnnouncementHtml } from '@/utils/sanitizeHtml';

interface FormState {
  targetApp: AnnouncementTargetApp;
  category: AnnouncementCategory;
  title: string;
  /** 본문 HTML (백엔드 키와 동일) */
  contentHtml: string;
  isImportant: boolean;
  isPinned: boolean;
  /** 게시 시작 — 일자/시/분 분리 입력. 일자 비어있으면 시작 제한 없음 */
  displayStartDate: string;
  displayStartHour: string;
  displayStartMinute: string;
  /** 게시 종료 — 일자/시/분 분리 입력. 일자 비어있으면 종료 제한 없음 */
  displayEndDate: string;
  displayEndHour: string;
  displayEndMinute: string;
}

/** 게시 시작 시·분 디폴트 (자정) */
const DEFAULT_START_HOUR = '00';
const DEFAULT_START_MINUTE = '00';
/** 게시 종료 시·분 디폴트 (당일 끝) */
const DEFAULT_END_HOUR = '23';
const DEFAULT_END_MINUTE = '59';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0'),
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0'),
);

function splitDateTime(value: string | null | undefined): {
  date: string;
  hour: string;
  minute: string;
} {
  if (!value) return { date: '', hour: '', minute: '' };
  // "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD"
  const [datePart = '', timePart = ''] = value.split(' ');
  const [hh = '', mm = ''] = timePart.split(':');
  return { date: datePart, hour: hh, minute: mm };
}

function joinDateTime(
  date: string,
  hour: string,
  minute: string,
  defaults: { hour: string; minute: string },
): string | undefined {
  if (!date) return undefined;
  const hh = (hour || defaults.hour).padStart(2, '0');
  const mm = (minute || defaults.minute).padStart(2, '0');
  return `${date} ${hh}:${mm}:00`;
}

const TARGET_OPTIONS: { value: AnnouncementTargetApp; label: string }[] = [
  { value: 'BUSINESS', label: '비즈니스앱' },
  { value: 'GUARDIAN', label: '보호자앱' },
];

const CATEGORY_OPTIONS: { value: AnnouncementCategory; label: string }[] = [
  { value: 'SYSTEM', label: '시스템 업데이트' },
  { value: 'MAINTENANCE', label: '점검 안내' },
  { value: 'EVENT', label: '이벤트' },
  { value: 'GENERAL', label: '일반 공지' },
];

interface Props {
  mode?: 'create' | 'edit';
}

export function AnnouncementCreatePage({ mode = 'create' }: Props) {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const editingId = mode === 'edit' && params.id ? Number(params.id) : null;
  const isEdit = editingId != null;

  const [form, setForm] = useState<FormState>({
    targetApp: 'BUSINESS',
    category: 'SYSTEM',
    title: '',
    contentHtml: '',
    isImportant: false,
    isPinned: false,
    displayStartDate: '',
    displayStartHour: DEFAULT_START_HOUR,
    displayStartMinute: DEFAULT_START_MINUTE,
    displayEndDate: '',
    displayEndHour: DEFAULT_END_HOUR,
    displayEndMinute: DEFAULT_END_MINUTE,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    data: existing,
    isLoading: isLoadingDetail,
    error: detailError,
    refetch,
  } = useQuery({
    queryKey: ['announcement', editingId],
    queryFn: () => announcementsApi.getAnnouncementDetail(editingId as number),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!isEdit || !existing) return;
    const start = splitDateTime(existing.displayStartAt);
    const end = splitDateTime(existing.displayEndAt);
    setForm({
      targetApp: existing.targetApp,
      category: existing.category,
      title: existing.title,
      contentHtml: existing.contentHtml,
      isImportant: existing.isImportant,
      isPinned: existing.isPinned,
      displayStartDate: start.date,
      displayStartHour: start.hour || DEFAULT_START_HOUR,
      displayStartMinute: start.minute || DEFAULT_START_MINUTE,
      displayEndDate: end.date,
      displayEndHour: end.hour || DEFAULT_END_HOUR,
      displayEndMinute: end.minute || DEFAULT_END_MINUTE,
    });
  }, [isEdit, existing]);

  const handleTextChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof FormState];
        return next;
      });
    }
  };

  const handleTargetChange = (value: AnnouncementTargetApp) => {
    setForm(prev => ({ ...prev, targetApp: value }));
  };

  const handleImportantToggle = (e: ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, isImportant: e.target.checked }));
  };

  const handlePinnedToggle = (e: ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, isPinned: e.target.checked }));
  };

  const stripHtml = (html: string): string =>
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

  const buildDisplayStart = (): string | undefined =>
    joinDateTime(
      form.displayStartDate,
      form.displayStartHour,
      form.displayStartMinute,
      { hour: DEFAULT_START_HOUR, minute: DEFAULT_START_MINUTE },
    );
  const buildDisplayEnd = (): string | undefined =>
    joinDateTime(
      form.displayEndDate,
      form.displayEndHour,
      form.displayEndMinute,
      { hour: DEFAULT_END_HOUR, minute: DEFAULT_END_MINUTE },
    );

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!form.title.trim()) next.title = '공지사항 제목을 입력해주세요.';
    if (!stripHtml(form.contentHtml)) {
      next.contentHtml = '공지사항 내용을 입력해주세요.';
    }
    const start = buildDisplayStart();
    const end = buildDisplayEnd();
    if (start && end && start > end) {
      next.displayEndDate = '종료 일시는 시작 일시 이후여야 합니다.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const start = buildDisplayStart();
      const end = buildDisplayEnd();
      if (isEdit && editingId != null) {
        // 백엔드 명세: displayStartAt/EndAt 가 undefined면 "변경 없음".
        // 명시적으로 비우려면 clear* 플래그 사용.
        const payload: AnnouncementUpdateInput = {
          targetApp: form.targetApp,
          category: form.category,
          title: form.title.trim(),
          contentHtml: sanitizeAnnouncementHtml(form.contentHtml),
          isImportant: form.isImportant,
          isPinned: form.isPinned,
        };
        if (start) {
          payload.displayStartAt = start;
        } else {
          payload.clearDisplayStart = true;
        }
        if (end) {
          payload.displayEndAt = end;
        } else {
          payload.clearDisplayEnd = true;
        }
        await announcementsApi.updateAnnouncement(editingId, payload);
        alert('공지사항이 수정되었습니다.');
        navigate(`/announcements/${editingId}`);
      } else {
        const payload: AnnouncementCreateInput = {
          targetApp: form.targetApp,
          category: form.category,
          title: form.title.trim(),
          contentHtml: sanitizeAnnouncementHtml(form.contentHtml),
          isImportant: form.isImportant,
          isPinned: form.isPinned,
          displayStartAt: start ?? null,
          displayEndAt: end ?? null,
        };
        await announcementsApi.createAnnouncement(payload);
        alert('공지사항이 등록되었습니다.');
        navigate('/announcements');
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : isEdit
            ? '공지사항 수정에 실패했습니다.'
            : '공지사항 등록에 실패했습니다.';
      alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEdit && isLoadingDetail) {
    return <LoadingState />;
  }
  if (isEdit && detailError) {
    return <ErrorState error={detailError} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={isEdit ? '공지사항 수정' : '공지사항 등록'}
        subtitle={
          isEdit
            ? '등록된 공지사항의 내용을 수정합니다.'
            : '대상 앱(비즈니스/보호자)을 선택해 시스템 공지를 등록합니다.'
        }
        right={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft size={16} />
              뒤로가기
            </button>
            <button
              type="button"
              onClick={() => setIsPreviewOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 lg:hidden"
            >
              <Eye size={16} />
              미리보기
            </button>
          </div>
        }
      />

      <AnnouncementPreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        data={{
          targetApp: form.targetApp,
          category: form.category,
          title: form.title,
          contentHtml: form.contentHtml,
          isImportant: form.isImportant,
          isPinned: form.isPinned,
          displayStartAt: buildDisplayStart(),
          displayEndAt: buildDisplayEnd(),
        }}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,420px)]">
        <form onSubmit={handleSubmit} className="space-y-4 min-w-0">
        <Section title="공지사항 정보">
          <div className="space-y-4">
            <Field label="대상 앱" required>
              <div className="flex flex-wrap gap-3">
                {TARGET_OPTIONS.map(opt => {
                  const checked = form.targetApp === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                        checked
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <input
                        type="radio"
                        name="targetApp"
                        value={opt.value}
                        checked={checked}
                        onChange={() => handleTargetChange(opt.value)}
                        disabled={isSubmitting}
                        className="accent-primary-500"
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </Field>

            <Field label="카테고리" required>
              <Select
                name="category"
                value={form.category}
                onChange={handleTextChange}
                disabled={isSubmitting}
                className="max-w-xs"
              >
                {CATEGORY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="제목" required error={errors.title}>
              <TextInput
                name="title"
                value={form.title}
                onChange={handleTextChange}
                placeholder="공지사항 제목을 입력하세요"
                disabled={isSubmitting}
              />
            </Field>

            <Field label="내용" required error={errors.contentHtml}>
              <RichTextEditor
                value={form.contentHtml}
                onChange={html => {
                  setForm(prev => ({ ...prev, contentHtml: html }));
                  if (errors.contentHtml) {
                    setErrors(prev => {
                      const next = { ...prev };
                      delete next.contentHtml;
                      return next;
                    });
                  }
                }}
                onUploadImage={announcementsApi.uploadImage}
                placeholder="공지사항 내용을 입력하세요. 굵게/링크/리스트 등 서식을 사용할 수 있습니다."
                disabled={isSubmitting}
              />
            </Field>

            <Field
              label="게시 기간"
              error={errors.displayEndDate}
              help="비워두면 즉시 노출 / 무기한 유지됩니다. 시·분을 비우면 시작은 00:00, 종료는 23:59로 적용됩니다."
            >
              <div className="space-y-2">
                <DateTimeRow
                  label="시작"
                  dateName="displayStartDate"
                  hourName="displayStartHour"
                  minuteName="displayStartMinute"
                  dateValue={form.displayStartDate}
                  hourValue={form.displayStartHour}
                  minuteValue={form.displayStartMinute}
                  onChange={handleTextChange}
                  disabled={isSubmitting}
                />
                <DateTimeRow
                  label="종료"
                  dateName="displayEndDate"
                  hourName="displayEndHour"
                  minuteName="displayEndMinute"
                  dateValue={form.displayEndDate}
                  hourValue={form.displayEndHour}
                  minuteValue={form.displayEndMinute}
                  onChange={handleTextChange}
                  disabled={isSubmitting}
                />
              </div>
            </Field>

            <Field
              label="공지 옵션"
              help="중요 공지는 목록에서 강조되고, 상단 고정은 항상 최상단에 노출됩니다."
            >
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isImportant}
                    onChange={handleImportantToggle}
                    disabled={isSubmitting}
                    className="h-4 w-4 accent-red-500"
                  />
                  중요 공지로 표시
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isPinned}
                    onChange={handlePinnedToggle}
                    disabled={isSubmitting}
                    className="h-4 w-4 accent-primary-500"
                  />
                  상단 고정
                </label>
              </div>
            </Field>

          </div>
        </Section>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send size={16} />
              {isSubmitting
                ? isEdit
                  ? '수정 중...'
                  : '등록 중...'
                : isEdit
                  ? '공지사항 수정'
                  : '공지사항 등록'}
            </button>
          </div>
        </form>

        {/* 데스크톱(lg+) 전용 sticky 미리보기 패널 */}
        <aside className="hidden lg:block">
          <PreviewAside
            data={{
              targetApp: form.targetApp,
              category: form.category,
              title: form.title,
              contentHtml: form.contentHtml,
              isImportant: form.isImportant,
              isPinned: form.isPinned,
              displayStartAt: buildDisplayStart(),
              displayEndAt: buildDisplayEnd(),
            }}
          />
        </aside>
      </div>
    </div>
  );
}

interface PreviewAsideProps {
  data: React.ComponentProps<typeof AnnouncementMobilePreview>['data'];
}
function PreviewAside({ data }: PreviewAsideProps) {
  const displayState = getDisplayState(data.displayStartAt, data.displayEndAt);
  const targetLabel =
    data.targetApp === 'BUSINESS' ? '비즈니스앱' : '보호자앱';

  return (
    <div className="sticky top-4">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">
              실시간 미리보기
            </h3>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
              {targetLabel}
            </span>
          </div>
          <span className={`text-[11px] font-medium ${displayState.tone}`}>
            {displayState.label}
          </span>
        </div>
        <div className="bg-[#ECECEC] p-4">
          <AnnouncementMobilePreview data={data} framed />
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  help?: string;
  children: React.ReactNode;
}
function Field({ label, required, error, help, children }: FieldProps) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {!error && help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  );
}

interface DateTimeRowProps {
  label: string;
  dateName: string;
  hourName: string;
  minuteName: string;
  dateValue: string;
  hourValue: string;
  minuteValue: string;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  disabled?: boolean;
}
function DateTimeRow({
  label,
  dateName,
  hourName,
  minuteName,
  dateValue,
  hourValue,
  minuteValue,
  onChange,
  disabled,
}: DateTimeRowProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <span className="w-10 text-xs font-medium text-gray-600">{label}</span>
      <TextInput
        type="date"
        name={dateName}
        value={dateValue}
        onChange={onChange}
        disabled={disabled}
        className="sm:w-44"
      />
      <div className="flex items-center gap-1">
        <Select
          name={hourName}
          value={hourValue}
          onChange={onChange}
          disabled={disabled}
          className="w-20"
        >
          {HOUR_OPTIONS.map(h => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Select>
        <span className="text-sm text-gray-500">시</span>
        <Select
          name={minuteName}
          value={minuteValue}
          onChange={onChange}
          disabled={disabled}
          className="w-20"
        >
          {MINUTE_OPTIONS.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
        <span className="text-sm text-gray-500">분</span>
      </div>
    </div>
  );
}
